import Service, { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { queryManager } from 'ember-apollo-client';
import { registerDestructor } from '@ember/destroyable';

import { compareSemVerGte } from 'my-phorest/utils/compare-semver';
import config from 'my-phorest/config/environment';

import openCashDrawer from 'my-phorest/gql/mutations/open-cash-drawer.graphql';
import registerPrintingDevice from 'my-phorest/gql/mutations/register-printing-device.graphql';

const RECEIPT_PRINTER_CONFIG_KEY = 'my-phorest:electron:receipt-printer';
export const ERROR_NO_PRINTER_SELECTED = 'NO_PRINTER_SELECTED';

export default class ElectronAppService extends Service {
  @service('browser/localStorage') localStorage;
  @service('browser/window') window;
  @service errorHandler;
  @service eventPropagator;
  @service intl;
  @service pendo;
  @service printerSetup;
  @service printingDevices;
  @service receiptSettings;
  @service revisionKey;
  @service session;

  @queryManager apollo;

  @tracked applicationInstanceId;
  @tracked friendlyDeviceName;
  @tracked isElectronPrinting;
  @tracked isCustomTitleBarEnabled = false;

  constructor() {
    super(...arguments);

    if (this.api?.pendoTrackEvent) {
      this.api.pendoTrackEvent((pendoEventName, properties) => {
        this.pendo.trackEvent(pendoEventName, properties);
      });
    }

    this.determineCustomTitleBarStatus();
    this.listenForFullscreenEvents();
  }

  get isRunningInElectron() {
    return !!this.window.electronAPI;
  }

  get api() {
    return this.window.electronAPI;
  }

  get hasCustomTitleBar() {
    return this.isCustomTitleBarEnabled;
  }

  determineCustomTitleBarStatus() {
    this.api?.hasCustomTitleBar?.().then((hasCustomTitleBar) => {
      this.isCustomTitleBarEnabled = hasCustomTitleBar;
    });
  }

  listenForFullscreenEvents() {
    this.api?.onFullscreenChanged?.((isFullscreen) => {
      this.eventPropagator.broadcast('fullscreenChanged', isFullscreen);
    });

    registerDestructor(this, () => {
      this.api?.removeFullscreenListener?.();
    });
  }

  launchSwing() {
    this.api?.launchSwing?.();
  }

  async isFullscreen() {
    return (await this.api?.isFullscreen?.()) ?? false;
  }

  async canLaunchSwing() {
    return await this.api?.canLaunchSwing?.();
  }

  async getElectronAppVersion() {
    return await this.api?.getAppVersion?.();
  }

  async getRemotePrintingTarget() {
    return await this.api?.loadRemotePrintingTarget?.();
  }

  async getVersion() {
    const electronAppVersion = await this.getElectronAppVersion();
    let revision = 'local';

    const { deployRevision, UNSPECIFIED_REVISION_KEY } = config.APP;
    if (deployRevision !== UNSPECIFIED_REVISION_KEY) {
      revision = deployRevision;
    }

    let version = `${electronAppVersion}-${revision.slice(0, 6)}`;

    if (
      config.deployTarget &&
      config.deployTarget !== 'production' &&
      config.deployTarget !== 'cloudfront-prod'
    ) {
      version = `${version} (${config.deployTarget})`;
    }

    return version;
  }

  async getPrinters() {
    return this.api?.getPrinters?.();
  }

  notifyDidSetupSentry(info) {
    this.api?.notifyDidSetupSentry?.(info);
  }

  async openCashDrawer({ onWarningCb, forceLocalOpening = false } = {}) {
    if (!forceLocalOpening) {
      const remoteTargetId = await this.getRemotePrintingTarget();

      if (remoteTargetId) {
        const result = await this.apollo.mutate(
          {
            mutation: openCashDrawer,
            variables: {
              printingDeviceId: remoteTargetId,
            },
          },
          'openCashDrawer'
        );
        this.printingDevices.showOpenCashDrawerRequestSentSuccessNotificationTask.perform(
          remoteTargetId
        );
        return result;
      }
    }

    const electronAppVersion = await this.getElectronAppVersion();
    const isNewCashDrawerApiElectron = compareSemVerGte(
      electronAppVersion,
      '10.9.0'
    );

    let printerName = this.receiptPrinterName;

    if (this.session.terminalId && isNewCashDrawerApiElectron) {
      const terminal = await this.receiptSettings.fetchTerminal();
      const virtualComPort = terminal?.receiptPrinterConfig?.port || null;

      this.api?.openCashDrawer?.(
        {
          printerName,
          virtualComPort,
        },
        (warning) => {
          if (typeof onWarningCb === 'function') {
            onWarningCb(warning);
          }
        }
      );
    } else {
      this.api?.openCashDrawer?.(printerName);
    }
  }

  /**
   * Registers this application instance (Electron app) as a Printing Device.
   * Registration doesn't happen when no printer is attached or there's no application instance id (Electron versions < 10.11).
   *
   * @returns {Promise<void>}
   */
  async maybeRegisterPrintingDevice() {
    if (!this.isRunningInElectron) return;
    if (!this.applicationInstanceId || !this.receiptPrinterName) return;

    if (this.session.isMultiBranch && !this.session.terminalId) {
      // Unlike the `if` above, this one might be temporary, depending on how the product will evolve.
      // We register a printing device in the context of a branch.
      // In single branch salons, we always know the branch context.
      // In multi-branch salons, we only know the context if a salon is being migrated from Swing and uses terminal access
      // because terminals are connected to branches.
      // Until we have a feature that lets the user say, "I only/mainly use this computer in branch X", we can't say for sure
      // what is their preferred branch-context - physically. They can switch between any branches in the app, but
      // they most likely sit in one of them.
      return;
    }

    let metadata = {
      computerName:
        this.friendlyDeviceName ||
        this.intl.t('print.default-printing-device-name'),
      printerName: this.receiptPrinterName,
      type: 'ELECTRON',
    };

    let terminal;
    if (this.session.terminalId) {
      terminal = await this.receiptSettings.fetchTerminal();
      metadata.terminalId = terminal.id;
      metadata.terminalName = terminal.name;
    }

    let branchId;
    if (this.session.isSingleBranch) {
      branchId = this.session.currentBranch.id;
    } else {
      branchId = terminal.preferredBranchId;
    }

    try {
      return await this.apollo.mutate(
        {
          mutation: registerPrintingDevice,
          variables: {
            id: this.applicationInstanceId,
            branchId,
            metadata,
          },
        },
        'registerPrintingDevice'
      );
    } catch (error) {
      this.errorHandler.handle(error, { showError: false });
    }
  }

  async printReceipt(url, callbacks, options) {
    let printerName = this.receiptPrinterName;

    const printURL = new URL(url);
    printURL.searchParams.append('printView', true);

    if (this.revisionKey.key) {
      printURL.searchParams.append('revision', this.revisionKey.key);
    }

    this.isElectronPrinting = true;

    this.api?.print?.(
      printerName,
      printURL.toString(),
      () => {
        this.isElectronPrinting = false;

        if (typeof callbacks?.successCb === 'function') {
          callbacks.successCb();
        }
      },
      options
    );
  }

  shutdown() {
    this.api?.shutdown?.();
  }

  minimize() {
    this.api?.minimize?.();
  }

  launchExternalURL(url) {
    this.api?.launchExternalURL?.(url);
  }

  saveReceiptPrinter(printerName) {
    this.receiptPrinterName = printerName;
    this.api?.saveReceiptPrinter?.(printerName);
  }

  saveRemotePrintingTarget(targetId) {
    if (targetId) {
      this.api?.saveRemotePrintingTarget?.(targetId);
    } else {
      this.api?.saveRemotePrintingTarget?.(null);
    }
  }

  async loadElectronContext() {
    await this.loadReceiptPrinter();
    await this.loadElectronIdentificationData();
  }

  async loadElectronIdentificationData() {
    this.applicationInstanceId = await this.api?.getAppInstanceId?.();
    if (!this.applicationInstanceId) return;

    this.friendlyDeviceName = await this.api?.getFriendlyDeviceName?.();
  }

  async loadReceiptPrinter() {
    let printerName = await this.api?.loadReceiptPrinter?.();

    this.receiptPrinterName = printerName;
  }

  get receiptPrinterName() {
    return this.localStorage.getItem(RECEIPT_PRINTER_CONFIG_KEY);
  }

  set receiptPrinterName(value) {
    if (value && value.trim?.() !== '') {
      this.localStorage.setItem(RECEIPT_PRINTER_CONFIG_KEY, value);
    } else {
      this.localStorage.removeItem(RECEIPT_PRINTER_CONFIG_KEY);
    }
  }

  async verifyHasPrinterToPrintTo() {
    let localReceiptPrinterName = this.receiptPrinterName;
    let remoteTargetId = await this.getRemotePrintingTarget();

    // No local printer. No remote printer. A user has to select something to print to
    if (isEmpty(localReceiptPrinterName) && isEmpty(remoteTargetId)) {
      const selected = await this.printerSetup.selectReceiptPrinter();

      if (isEmpty(selected.printerValue)) {
        const error = new Error('No printer selected');
        error.code = ERROR_NO_PRINTER_SELECTED;
        throw error;
      }

      if (selected.mode === 'LOCAL') {
        localReceiptPrinterName = selected.printerValue;
        this.saveReceiptPrinter(localReceiptPrinterName);
      } else {
        remoteTargetId = selected.printerValue;
        this.saveRemotePrintingTarget(remoteTargetId);
      }
    }

    return { localReceiptPrinterName, remoteTargetId };
  }

  get canDownloadFile() {
    return !!this.api?.downloadFile;
  }

  downloadFile(url, options) {
    if (this.api?.downloadFile) {
      return this.api.downloadFile(url, options);
    }
  }
}
